import { getAuthInstance } from '@zealth/core/firebase/runtime'
import { onAuthStateChanged, type User } from 'firebase/auth'
import { useEffect, useState } from 'react'

import { type PlaygroundRole, resolvePlaygroundRole } from './playgroundRole'

export type PlaygroundSession = {
  user: User | null
  /** Null while loading, and null for a signed-in account with no role. */
  role: PlaygroundRole | null
  isLoading: boolean
}

/**
 * Who is signed in and what they may do.
 *
 * The token is force-refreshed on every auth state change, because claims reach
 * a client only when the token is renewed — a role granted while somebody was
 * signed in would otherwise take up to an hour to appear.
 */
export const usePlaygroundSession = (): PlaygroundSession => {
  const auth = getAuthInstance()
  const [session, setSession] = useState<PlaygroundSession>({
    user: null,
    role: null,
    isLoading: true,
  })

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async user => {
      if (!user) {
        setSession({ user: null, role: null, isLoading: false })
        return
      }

      const { claims } = await user.getIdTokenResult(true)
      setSession({
        user,
        role: resolvePlaygroundRole(claims),
        isLoading: false,
      })
    })

    return (): void => unsubscribe()
  }, [auth])

  return session
}
